"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import AdCarousel from "@/components/AdCarousel";
import {
  Search,
  Package,
  MapPin,
  Store,
  ShieldCheck,
  ChevronRight,
  Star,
  Building2,
  Truck,
  CheckCircle2,
  ArrowRight,
  Filter,
  Heart,
  BadgeCheck,
  Headset,
  Camera,
  Sparkles,
  BriefcaseBusiness,
  Trophy,
  TrendingUp,
  Calendar,
  BookOpen,
  Newspaper,
  Users,
  type LucideIcon,
} from "lucide-react";
import Image from "next/image";
import { type FormEvent, useEffect, useState } from "react";
import { PackageSearch, Factory } from "lucide-react";
import { Boxes, Cpu, Shirt, Wrench, Plug } from "lucide-react";

import { cn } from "@/lib/utils";

import {
  fetchWelcomeData,
  fetchWelcomeCategories,
  type WelcomeCategory,
  type WelcomeCategoryDirectorySection,
  type WelcomeProduct,
  type WelcomeSupplier,
} from "@/services/welcomeService";

const categoryIcons = [
  Cpu,
  Factory,
  Shirt,
  Wrench,
  Truck,
  Plug,
  Package,
  Boxes,
];
const trustFactors = [
  {
    title: "Verified supplier discovery",
    description: "Explore supplier profiles with a clear verified-status signal.",
    icon: BadgeCheck,
  },
  {
    title: "Product details that matter",
    description: "Compare price, MOQ, supplier, and location in one view.",
    icon: Package,
  },
  {
    title: "Guided trade support",
    description: "Get help when your sourcing requirement needs context.",
    icon: Headset,
  },
];

const numberFormatter = new Intl.NumberFormat("en-IN");

const formatCompactCount = (value: number) =>
  Number(value || 0) > 0 ? numberFormatter.format(Number(value || 0)) : "0";

const buildCategoryHref = (query: string) => {
  const params = new URLSearchParams();

  if (query.trim()) {
    params.set("q", query.trim());
  }

  return params.toString()
    ? `/welcome/categories?${params.toString()}`
    : "/welcome/categories";
};

type SearchExperienceId =
  | "ai"
  | "products"
  | "manufacturers"
  | "worldwide"
  | "services"
  | "verified-suppliers"
  | "trusted-sellers";

type FeaturedProductFilterId = "top_picks" | "verified" | "ready_to_source";

const featuredProductFilters: Array<{
  id: FeaturedProductFilterId;
  label: string;
}> = [
  { id: "top_picks", label: "Top Picks" },
  { id: "verified", label: "Verified" },
  { id: "ready_to_source", label: "Ready to Source" },
];

const searchExperiences: {
  id: SearchExperienceId;
  label: string;
  mobileLabel: string;
  icon: LucideIcon;
  route: string;
  searchParams?: Record<string, string>;
  placeholder: string;
  description: string;
}[] = [
  {
    id: "products",
    label: "Products",
    mobileLabel: "Prod",
    icon: Package,
    route: "/welcome/products",
    placeholder: "smart watch for women",
    description:
      "Search verified products, SKUs, and supplier catalogs with a cleaner focused search flow.",
  },
  {
    id: "services",
    label: "Services",
    mobileLabel: "Svc",
    icon: BriefcaseBusiness,
    route: "/welcome/categories",
    placeholder: "inspection, freight, packaging, sourcing services",
    description:
      "Browse trade-support services like inspection, logistics, packaging, and sourcing assistance.",
  },
  {
    id: "manufacturers",
    label: "Manufacturers",
    mobileLabel: "Makers",
    icon: Factory,
    route: "/welcome/suppliers",
    placeholder: "smart watch manufacturers in india",
    description:
      "Jump directly into supplier profiles, response rates, and capabilities from the same search surface.",
  },
  // {
  //   id: "worldwide",
  //   label: "Worldwide",
  //   icon: Globe2,
  //   route: "/welcome/categories",
  //   placeholder: "global sourcing categories and export-ready products",
  //   description:
  //     "Use the same prominent input to broaden discovery across marketplace sections and trade-ready categories.",
  // },

  {
    id: "verified-suppliers",
    label: "Verified Suppliers",
    mobileLabel: "Verify",
    icon: BadgeCheck,
    route: "/welcome/suppliers",
    searchParams: { verified: "true" },
    placeholder: "verified suppliers for electronics",
    description:
      "Explore verified suppliers with trusted profiles, catalog depth, and stronger sourcing confidence.",
  },
  {
    id: "trusted-sellers",
    label: "DoTrust Sellers",
    mobileLabel: "Seller",
    icon: ShieldCheck,
    route: "/welcome/suppliers",
    searchParams: { verified: "true" },
    placeholder: "trusted sellers for wholesale products",
    description:
      "Find trusted sellers with reliable listings, stronger credibility, and safer marketplace decisions.",
  },
  {
    id: "ai",
    label: "AI Mode",
    mobileLabel: "AI",
    icon: Sparkles,
    route: "/welcome/products",
    placeholder: "Describe what you need and let AI refine the search",
    description:
      "Conversational sourcing styling is in place, and the search continues into the product catalog.",
  },
];

const heroSearchPills: Array<
  | {
      type: "experience";
      id: SearchExperienceId;
      label: string;
      icon: LucideIcon;
    }
  | {
      type: "link";
      href: string;
      label: string;
      icon: LucideIcon;
    }
> = [
  {
    type: "experience",
    id: "ai",
    label: "AI Mode",
    icon: Sparkles,
  },
  {
    type: "experience",
    id: "products",
    label: "Products",
    icon: Package,
  },
  {
    type: "experience",
    id: "manufacturers",
    label: "Manufacturers",
    icon: Factory,
  },
  {
    type: "link",
    href: "/welcome/about",
    label: "Services",
    icon: Headset,
  },
  {
    type: "link",
    href: "/welcome/suppliers",
    label: "Verified Suppliers",
    icon: BadgeCheck,
  },
  {
    type: "link",
    href: "/welcome/suppliers",
    label: "Trusted Sellers",
    icon: ShieldCheck,
  },
];

const searchHeroPlaceholderHints = [
  "Try: Solar Panels...",
  "Try: LED Strip Lights...",
  "Try: Stainless Steel Pipes...",
  "Try: Cotton Fabric...",
  "Try: Industrial Motors...",
];

const searchHeroWorldMapSvg = "/assets/world-map-compact.svg";

/* ---------------- COMPONENT ---------------- */

function SectionEmptyState({
  icon: Icon,
  eyebrow,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  eyebrow?: string;
  title: string;
  description: string;
  action?: { label: string; href: string };
}) {
  return (
    <div className="relative col-span-full overflow-hidden rounded-[1.5rem] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fbff_58%,#fffdf3_100%)] px-5 py-10 text-center shadow-[0_24px_60px_-46px_rgba(15,27,53,0.5)] dark:border-slate-800 dark:bg-[linear-gradient(135deg,rgba(15,23,42,0.96)_0%,rgba(15,23,42,0.95)_58%,rgba(54,47,0,0.32)_100%)] sm:px-8 sm:py-12">
      <div className="pointer-events-none absolute -left-12 top-1/2 h-32 w-32 -translate-y-1/2 rounded-full bg-yellow-200/20 blur-3xl dark:bg-amber-400/10" />
      <div className="pointer-events-none absolute -right-12 top-0 h-36 w-36 rounded-full bg-sky-200/45 blur-3xl dark:bg-sky-400/10" />
      <div className="relative mx-auto max-w-md">
        {eyebrow ? (
          <span className="inline-flex rounded-full border border-yellow-300 bg-yellow-50 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#0F1B35] dark:border-amber-300/30 dark:bg-amber-400/10 dark:text-amber-300">
            {eyebrow}
          </span>
        ) : null}
        <div className="mx-auto mt-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0F1B35] text-white shadow-[0_10px_24px_rgba(15,27,53,0.2)] dark:bg-amber-400 dark:text-slate-950">
          <Icon className="h-6 w-6" />
        </div>
        <h3 className="mt-5 text-lg font-semibold tracking-tight text-[#0F1B35] dark:text-white sm:text-xl">
          {title}
        </h3>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-600 dark:text-slate-300">
          {description}
        </p>
        {action ? (
          <Button
            asChild
            className="mt-5 h-10 rounded-lg bg-[#0F1B35] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#182950]"
          >
            <Link href={action.href}>
              {action.label}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function HomePage() {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchExperience, setActiveSearchExperience] =
    useState<SearchExperienceId>("products");
  const [activeFeaturedProductFilter, setActiveFeaturedProductFilter] =
    useState<FeaturedProductFilterId>("top_picks");
  const [categories, setCategories] = useState<WelcomeCategory[]>([]);
  const [categorySections, setCategorySections] = useState<
    WelcomeCategoryDirectorySection[]
  >([]);
  const [products, setProducts] = useState<WelcomeProduct[]>([]);
  const [wishlistedProductIds, setWishlistedProductIds] = useState<string[]>(
    [],
  );
  const [featuredSuppliers, setFeaturedSuppliers] = useState<WelcomeSupplier[]>(
    [],
  );
  const activeSearchConfig =
    searchExperiences.find((item) => item.id === activeSearchExperience) ||
    searchExperiences[1];
  const ActiveSearchIcon = activeSearchConfig.icon;
  const categoryDirectoryEntries =
    categorySections.length > 0
      ? categorySections.slice(0, 12).map((section) => ({
          id: section.id,
          name: section.name,
          count: section.productCount,
          description: section.description || "",
          href: buildCategoryHref(section.name),
          groups: section.groups,
        }))
      : categories.slice(0, 16).map((category) => ({
          id: category.id || category.name,
          name: category.name,
          count: category.count,
          description: category.description || "",
          href: buildCategoryHref(category.name),
          groups: [] as WelcomeCategoryDirectorySection["groups"],
        }));
  const filteredFeaturedProducts = products.filter((product) => {
    switch (activeFeaturedProductFilter) {
      case "verified":
        return product.verified;
      case "ready_to_source":
        return Boolean(product.price && product.moq && product.location);
      case "top_picks":
      default:
        return Number(product.rating) >= 4.8;
    }
  });
  const visibleFeaturedProducts =
    activeFeaturedProductFilter === "top_picks" &&
    filteredFeaturedProducts.length === 0
      ? products
      : filteredFeaturedProducts;

  useEffect(() => {
    const loadWelcomeData = async () => {
      try {
        const payload = await fetchWelcomeData();
        setCategories(
          Array.isArray(payload.categories) ? payload.categories : [],
        );
        setProducts(Array.isArray(payload.products) ? payload.products : []);
        setFeaturedSuppliers(
          Array.isArray(payload.featuredSuppliers)
            ? payload.featuredSuppliers
            : [],
        );
      } catch (error) {
        setCategories([]);
        setProducts([]);
        setFeaturedSuppliers([]);
      }
    };

    loadWelcomeData();
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadCategorySections = async () => {
      try {
        const payload = await fetchWelcomeCategories({ limit: 12 });

        if (!isActive) return;

        setCategorySections(
          Array.isArray(payload.sections) ? payload.sections : [],
        );
      } catch (error) {
        if (!isActive) return;
        setCategorySections([]);
      }
    };

    loadCategorySections();

    return () => {
      isActive = false;
    };
  }, []);
  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedQuery = searchQuery.trim();
    const params = new URLSearchParams();

    if (trimmedQuery) {
      params.set("q", trimmedQuery);
    }

    Object.entries(activeSearchConfig.searchParams || {}).forEach(
      ([key, value]) => params.set(key, value),
    );

    router.push(
      params.toString()
        ? `${activeSearchConfig.route}?${params.toString()}`
        : activeSearchConfig.route,
    );
  };

  const handleFeaturedProductFilterChange = (
    filterId: FeaturedProductFilterId,
  ) => {
    setActiveFeaturedProductFilter(filterId);
    console.log("[FeaturedProducts] selected filter:", filterId);
  };

  const toggleFeaturedProductWishlist = (productId: string) => {
    setWishlistedProductIds((currentIds) =>
      currentIds.includes(productId)
        ? currentIds.filter((id) => id !== productId)
        : [...currentIds, productId],
    );
  };

  const openFeaturedProduct = (productId: string) => {
    router.push(`/welcome/products/${productId}`);
  };

  const navItems = [
    { name: "Top Rankers", icon: Trophy },
    { name: "Most Profitables", icon: TrendingUp },
    { name: "New Products", icon: Package },
    { name: "Sourcing Events & Expos", icon: Calendar },
    { name: "DoLearn Export", icon: BookOpen },
    { name: "B2B Do Trade Vlogs", icon: Newspaper },
    { name: "DoExim networking", icon: Users },
  ];
  return (
    <div className="w-full">
      {/* SEARCH BAR */}
      <section className="relative overflow-hidden border-b border-yellow-300 bg-[#f4d03f] dark:border-slate-800 dark:bg-[linear-gradient(180deg,#08111f_0%,#0f1c2f_58%,#152844_100%)]">
        {" "}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(880px_420px_at_-6%_-12%,rgba(255,186,126,0.1),transparent_66%)] dark:bg-[radial-gradient(900px_420px_at_-8%_-14%,rgba(148,163,184,0.12),transparent_68%)]" />
        <div className="pointer-events-none absolute -right-12 bottom-0 h-48 w-48 rounded-full bg-sky-200/50 blur-3xl dark:bg-cyan-300/10" />
        <div className="container relative mx-auto px-4 py-8 sm:px-6 sm:py-10">
          <div className="mx-auto max-w-5xl">
            <div className="w-full pb-2">
              <div className="mx-auto grid grid-cols-6 gap-1 px-0 sm:flex sm:min-w-max sm:items-center sm:justify-center sm:px-0 md:min-w-0 md:flex-wrap">
                {searchExperiences.map(
                  ({ id, label, mobileLabel, icon: Icon }) => {
                    const isActive = id === activeSearchExperience;

                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setActiveSearchExperience(id)}
                        className={cn(
                          "group inline-flex min-h-[34px] w-full items-center justify-center rounded-[8px] px-1 py-1 text-[8px] font-semibold leading-none tracking-tight transition-all duration-200 sm:h-[44px] sm:min-h-0 sm:w-auto sm:shrink-0 sm:flex-row sm:gap-1.5 sm:rounded-[14px] sm:px-4 sm:py-0 sm:text-[13px] md:h-[46px]",
                          isActive
                            ? "border border-[#d7b62a] bg-[linear-gradient(180deg,#fff8d8_0%,#f6e27a_100%)] text-[#1f1a00] shadow-[inset_0_2px_0_rgba(255,255,255,0.85),inset_0_-2px_0_rgba(181,140,0,0.22),0_3px_0_#c89d12,0_10px_18px_rgba(120,85,0,0.16)] dark:border-amber-300/35 dark:bg-[linear-gradient(180deg,rgba(250,204,21,0.22)_0%,rgba(245,158,11,0.15)_100%)] dark:text-amber-50 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_10px_24px_rgba(0,0,0,0.34)]"
                            : "border border-[#d8d2c8] bg-[#f3f1ea] text-slate-700 shadow-[0_1px_0_rgba(255,255,255,0.75)] hover:bg-white hover:text-slate-900 dark:border-slate-700/80 dark:bg-slate-900/70 dark:text-slate-300 dark:shadow-none dark:hover:bg-slate-800 dark:hover:text-white",
                        )}
                        aria-pressed={isActive}
                      >
                        <Icon
                          className={cn(
                            "hidden shrink-0 transition-colors sm:block sm:h-3.5 sm:w-3.5",
                            isActive
                              ? "text-[#1f1a00]"
                              : "text-slate-500 group-hover:text-[#1f1a00]",
                          )}
                        />
                        <span className="text-center whitespace-nowrap sm:hidden">
                          {mobileLabel}
                        </span>
                        <span className="hidden text-center whitespace-nowrap sm:inline">
                          {label}
                        </span>
                      </button>
                    );
                  },
                )}
              </div>
            </div>
            <form
              onSubmit={handleSearchSubmit}
              className="
    mt-5
    rounded-2xl
    border border-yellow-300
    bg-gradient-to-b from-[#f7d93b] to-[#e6c229]
    p-[6px]
    shadow-[0_8px_20px_rgba(120,85,0,0.18)]
    dark:border-slate-700/80
    dark:bg-[linear-gradient(180deg,rgba(30,41,59,0.96)_0%,rgba(15,23,42,0.98)_100%)]
    dark:shadow-[0_18px_38px_rgba(2,6,23,0.42)]
  "
            >
              <div
                className="
      flex items-center gap-3
      rounded-xl
      bg-[#f9e15a]
      px-4 py-2
      shadow-[inset_0_2px_4px_rgba(255,255,255,0.4),inset_0_-2px_4px_rgba(0,0,0,0.08)]
      dark:bg-[linear-gradient(180deg,rgba(15,23,42,0.96)_0%,rgba(17,24,39,0.98)_100%)]
      dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05),inset_0_-1px_0_rgba(0,0,0,0.45)]
    "
              >
                {/* LEFT CONTENT */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#1f1a00] dark:text-amber-300">
                    <ActiveSearchIcon className="h-3.5 w-3.5" />
                    {activeSearchConfig.label}
                  </div>

                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={activeSearchConfig.placeholder}
                    className="
          mt-1
          border-0 bg-transparent
          p-0
          text-[15px] font-medium
          text-[#2b2200]
          placeholder:text-[#7a6a1c]
          focus-visible:ring-0
          dark:text-slate-100
          dark:placeholder:text-slate-500
        "
                  />
                </div>

                {/* IMAGE SEARCH */}
                <button
                  type="button"
                  className="
        hidden sm:flex items-center gap-2
        rounded-full
        bg-white/70
        px-3 py-1.5
        text-xs font-semibold
        text-slate-800
        hover:bg-white
        transition
        dark:bg-slate-800/90
        dark:text-slate-200
        dark:hover:bg-slate-700
      "
                >
                  <Camera className="h-4 w-4 text-[#1f1a00] dark:text-amber-300" />
                  Image
                </button>

                {/* SEARCH BUTTON */}
                <Button
                  type="submit"
                  aria-label="Search"
                  className="
    !h-[52px] !w-[60px]
    shrink-0 self-center
    !min-w-0 !px-0 !py-0
    rounded-xl border-0
    bg-gradient-to-b from-[#f8e63a] to-[#eed52a]
    text-[#1f1a00]
    shadow-[0_3px_0_#bb9710,0_10px_16px_rgba(120,85,0,0.28),inset_0_1px_0_rgba(255,255,255,0.34),inset_0_-1px_0_rgba(0,0,0,0.06)]
    transition-all duration-150
    dark:bg-[linear-gradient(180deg,#f59e0b_0%,#d97706_100%)]
    dark:text-slate-950
    dark:shadow-[0_3px_0_#92400e,0_14px_22px_rgba(0,0,0,0.4),inset_0_1px_0_rgba(255,255,255,0.16)]

    hover:translate-y-[1px]
    hover:shadow-[0_2px_0_#bb9710,0_8px_12px_rgba(120,85,0,0.24),inset_0_1px_0_rgba(255,255,255,0.34),inset_0_-1px_0_rgba(0,0,0,0.06)]
    dark:hover:shadow-[0_2px_0_#92400e,0_12px_18px_rgba(0,0,0,0.34),inset_0_1px_0_rgba(255,255,255,0.16)]

    active:translate-y-[3px]
    active:shadow-[0_0_0_#bb9710,0_3px_6px_rgba(120,85,0,0.18),inset_0_2px_3px_rgba(0,0,0,0.08)]
    dark:active:shadow-[0_0_0_#92400e,0_3px_8px_rgba(0,0,0,0.28),inset_0_2px_3px_rgba(0,0,0,0.18)]
  "
                >
                  <Search className="!h-8 !w-8 stroke-[2.2]" />
                </Button>
              </div>
            </form>
          </div>
        </div>
        <div className="hidden container mx-auto px-4 sm:px-6">
          {/* Top Row */}
          <div className="flex items-center gap-4 py-4">
            <div className="flex-1">
              <div className="relative">
                <Input
                  placeholder="Search products, categories, suppliers…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="
              h-12 w-full
              rounded-xl
              bg-muted/60
              border-border
              pl-5 pr-28
              shadow-sm
              focus-visible:ring-2 focus-visible:ring-primary/30
              focus-visible:bg-background
            "
                />

                <Button
                  size="sm"
                  className="
              absolute right-1.5 top-1.5
              h-9 px-4 gap-1.5
              rounded-lg
              shadow-sm
            "
                >
                  <Search className="h-4 w-4" />
                  <span className="hidden sm:inline">Search</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="-mx-4 px-4 sm:mx-0 sm:px-0 flex items-center gap-2 pb-4 overflow-x-auto scrollbar-none">
            <Button
              variant="secondary"
              size="sm"
              className="gap-1.5 rounded-full shrink-0 whitespace-nowrap"
            >
              <Filter className="h-4 w-4" />
              All Categories
            </Button>

            <div className="h-5 w-px bg-border/60 shrink-0" />

            {["Products", "Suppliers", "Buying Requests"].map((item) => (
              <Button
                key={item}
                variant="ghost"
                size="sm"
                className="
            rounded-full px-4
            text-muted-foreground
            hover:text-foreground
            hover:bg-muted/60
            shrink-0 whitespace-nowrap
          "
              >
                {item}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-[1720px] px-2 sm:px-3 lg:px-4">
          <div
            className="
      flex min-h-[44px] items-center
      gap-4 lg:gap-8
      overflow-x-auto whitespace-nowrap
      py-2 text-[10px] font-medium text-slate-600
      scrollbar-none sm:text-[11px] lg:text-xs
      justify-center
    "
          >
            {navItems.map((item, index) => {
              const Icon = item.icon;

              return (
                <div key={item.name} className="flex items-center">
                  <Link
                    href="#"
                    className="flex items-center gap-[2px] px-1 whitespace-nowrap text-[11px]"
                  >
                    <Icon className="h-[12px] w-[12px] text-slate-500" />
                    {item.name}
                  </Link>

                  {index !== navItems.length - 1 && (
                    <div className="mx-1 h-3 w-px bg-slate-300"></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* CATEGORIES */}
      <section className="bg-white dark:bg-slate-950">
        <div className="container mx-auto px-4 py-10 sm:px-6 sm:py-12">
          {categoryDirectoryEntries.length > 0 ? (
            <div className="rounded-[1.5rem] border border-slate-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#f8fbff_60%,#eef6ff_100%)] p-4 shadow-[0_28px_70px_-50px_rgba(15,23,42,0.45)] sm:rounded-[2rem] sm:p-6 dark:border-slate-800 dark:bg-[linear-gradient(135deg,rgba(15,23,42,0.96)_0%,rgba(15,23,42,0.94)_56%,rgba(8,47,73,0.9)_100%)]">
              {/* HEADER */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-slate-950 dark:text-white">
                    <Package className="h-4.5 w-4.5 text-primary" />
                    <h2 className="text-xl font-semibold sm:text-2xl">
                      Browse categories
                    </h2>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                    Pick a category and explore products & suppliers
                  </p>
                </div>
              </div>

              {/* MAIN LAYOUT */}
              <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
                <div className="lg:col-span-1">
                  <AdCarousel />
                </div>

                {/* RIGHT: CATEGORIES */}
                <div className="lg:col-span-2">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 auto-rows-fr">
                    {categoryDirectoryEntries.slice(0, 5).map((cat, idx) => {
                      const Icon =
                        categoryIcons[idx % categoryIcons.length] || Package;

                      return (
                        <Link
                          key={cat.id}
                          href={cat.href}
                          className="group flex h-[130px] flex-col justify-between rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-1 hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
                        >
                          <span className="h-10 w-10 flex items-center justify-center rounded-lg bg-slate-100 group-hover:bg-yellow-100">
                            <Icon className="h-4.5 w-4.5" />
                          </span>

                          <div>
                            <p className="text-sm font-semibold group-hover:text-[#0F1B35]">
                              {cat.name}
                            </p>

                            {cat.count > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                {formatCompactCount(cat.count)} products
                              </p>
                            )}
                          </div>
                        </Link>
                      );
                    })}

                    <Link
                      href="/welcome/categories"
                      className="group flex h-[130px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-center transition hover:border-yellow-400 hover:bg-yellow-50"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 text-[#0F1B35] mb-2">
                        <ChevronRight className="h-5 w-5" />
                      </div>

                      <p className="text-sm font-semibold text-slate-800 group-hover:text-[#0F1B35]">
                        View all categories
                      </p>

                      <span className="text-xs text-slate-500 mt-1">
                        Explore full catalog
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <SectionEmptyState
              icon={PackageSearch}
              title="No categories available"
              description="Categories will appear here"
            />
          )}
        </div>
      </section>
      {/* FEATURED PRODUCTS */}
      <section className="bg-slate-50 dark:bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 py-14">
          <div className="overflow-hidden rounded-[1.5rem] border border-slate-200/80 bg-[linear-gradient(135deg,#ffffff_0%,#f8fbff_56%,#eef6ff_100%)] p-3 shadow-[0_28px_70px_-50px_rgba(15,23,42,0.45)] sm:rounded-[2rem] sm:p-4 dark:border-slate-800 dark:bg-[linear-gradient(135deg,rgba(15,23,42,0.96)_0%,rgba(15,23,42,0.94)_56%,rgba(8,47,73,0.9)_100%)]">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div className="min-w-0">
                  <div className="border-l-[3px] border-[#f4d03f] pl-3">
                    <h2 className="text-[20px] font-bold text-[#0F1B35]">
                      Marketplace products
                    </h2>
                    <p className="mt-0.5 text-[13px] text-[#6B7280]">
                      Explore product opportunities as new suppliers join the marketplace.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  {featuredProductFilters.map((filter) => {
                    const isActive = activeFeaturedProductFilter === filter.id;

                    return (
                      <button
                        key={filter.id}
                        type="button"
                        onClick={() =>
                          handleFeaturedProductFilterChange(filter.id)
                        }
                        className={cn(
                          "rounded-full border bg-white px-4 py-1.5 text-[12px] font-medium uppercase tracking-[0.03em] transition-all duration-150",
                          isActive
                            ? "border-[#0F1B35] bg-[#0F1B35] text-white"
                            : "border-[#E5E7EB] text-[#6B7280] hover:border-[#d7b62a] hover:text-[#0F1B35]",
                        )}
                      >
                        {filter.label}
                      </button>
                    );
                  })}
                </div>

                <Link
                  href="/welcome/products"
                  className="inline-flex items-center gap-1 text-[13px] font-medium text-[#0F1B35] transition hover:underline"
                >
                  View all
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>

              {visibleFeaturedProducts.length > 0 ? (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
                  {visibleFeaturedProducts.map((product) => {
                    const isWishlisted = wishlistedProductIds.includes(
                      product.id,
                    );

                    return (
                      <article
                        key={product.id}
                        role="link"
                        tabIndex={0}
                        onClick={() => openFeaturedProduct(product.id)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openFeaturedProduct(product.id);
                          }
                        }}
                        className="group cursor-pointer overflow-hidden rounded-xl border border-[#E5E7EB] bg-white transition duration-200 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(15,27,53,0.12)]"
                      >
                        <div className="relative h-[180px] w-full overflow-hidden bg-[#F3F4F6]">
                          <Image
                            src={product.image?.trim() || "/no-image.png"}
                            alt={product.name}
                            width={320}
                            height={320}
                            className="h-full w-full object-cover object-center"
                          />

                          <div className="absolute left-2 top-2 flex flex-col gap-1">
                            {product.verified ? (
                              <span className="inline-flex items-center rounded-[5px] bg-[rgba(15,27,53,0.75)] px-2 py-1 text-[10px] font-medium text-white">
                                {"\u2713"} Verified
                              </span>
                            ) : null}
                            {Number(product.rating) >= 4.8 ? (
                              <span className="inline-flex rounded-[4px] bg-[#0F1B35] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.06em] text-white">
                                Top Rated
                              </span>
                            ) : null}
                          </div>

                          <button
                            type="button"
                            aria-label={
                              isWishlisted
                                ? "Remove from wishlist"
                                : "Add to wishlist"
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleFeaturedProductWishlist(product.id);
                            }}
                            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white text-[#9CA3AF] shadow-[0_1px_4px_rgba(0,0,0,0.15)] transition hover:text-[#EF4444]"
                          >
                            <Heart
                              className={cn(
                                "h-[14px] w-[14px]",
                                isWishlisted
                                  ? "fill-[#EF4444] text-[#EF4444]"
                                  : "",
                              )}
                            />
                          </button>
                        </div>

                        <div className="px-4 pb-4 pt-3.5">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="min-h-[40px] text-[14px] font-semibold leading-[1.45] text-[#0F1B35] line-clamp-2">
                              {product.name}
                            </h3>
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-1.5 py-1 text-[10px] font-semibold text-[#8a5b00]">
                              <Star className="h-3 w-3 fill-[#F59E0B] text-[#F59E0B]" />
                              {product.rating.toFixed(1)}
                            </span>
                          </div>

                          <p className="mt-2 truncate text-[11px] font-semibold uppercase tracking-[0.06em] text-[#7B8494]">
                            {product.supplier}
                          </p>

                          <div className="mt-3 flex items-start justify-between gap-3 rounded-lg bg-[#F6F8FC] px-3 py-2.5">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-[15px] font-bold text-[#0F1B35]">
                                {product.price.replace(/\s*-\s*/g, " – ")}
                              </p>
                              <span className="mt-0.5 block text-[10px] text-[#7B8494]">
                                Price per {product.unit || "unit"}
                              </span>
                            </div>

                            <div className="shrink-0 border-l border-[#DDE4EF] pl-3 text-right">
                              <span className="block text-[9px] font-bold uppercase tracking-[0.08em] text-[#7B8494]">
                                MOQ
                              </span>
                              <span className="mt-0.5 block text-[12px] font-semibold text-[#0F1B35]">
                                {product.moq}
                              </span>
                            </div>
                          </div>

                          <div className="mt-3 flex items-center justify-between gap-3 border-t border-[#EEF1F5] pt-3">
                            <span className="flex min-w-0 items-center gap-1.5 truncate text-[11px] text-[#7B8494]">
                              <MapPin className="h-3.5 w-3.5 shrink-0 text-[#0F1B35]" />
                              {product.location}
                            </span>
                            <span className="inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold text-[#0F1B35]">
                              View product
                              <span aria-hidden="true">&rarr;</span>
                            </span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <SectionEmptyState
                  icon={Package}
                  eyebrow="Marketplace catalog"
                  title="Fresh product listings are on their way"
                  description="Start with a category today, then return to discover newly published products and supplier catalogs."
                  action={{
                    label: "Browse categories",
                    href: "/welcome/categories",
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED SUPPLIERS */}
      <section className="bg-slate-100 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
        {" "}
        <div className="container mx-auto px-4 sm:px-6 py-14">
          {/* Header */}
          <div className="mb-6 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">
                Supplier network
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Meet manufacturers and exporters as they build their DoExim presence.
              </p>
            </div>

            <Button
              asChild
              variant="ghost"
              size="sm"
              className="hidden sm:inline-flex shrink-0 gap-1 text-muted-foreground hover:text-foreground hover:bg-slate-100 dark:hover:bg-slate-800 px-3 py-2 rounded-lg transition"
            >
              <Link href="/welcome/suppliers">
                View all
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* Grid */}
          <div
            className="
        grid grid-cols-1
        min-[520px]:grid-cols-2
        lg:grid-cols-4
        gap-5
      "
          >
            {featuredSuppliers.length > 0 ? (
              featuredSuppliers.map((supplier) => (
                <Link
                  key={supplier.id}
                  href={`/welcome/suppliers/${supplier.id}`}
                  className="
            group cursor-pointer
            rounded-xl border
            bg-card
            px-5 py-5
            transition-all
            hover:shadow-lg
            hover:-translate-y-0.5
          "
                >
                  {/* Top */}
                  <div className="flex items-start gap-3">
                    {/* Logo */}
                    <div
                      className="
                flex h-12 w-12
                items-center justify-center
                rounded-xl border bg-slate-50
                overflow-hidden
                shrink-0
              "
                    >
                      <Image
                        src={supplier.logo?.trim() || "/s-no-image.png"}
                        alt={supplier.name}
                        width={48}
                        height={48}
                        className="object-cover"
                        onError={(event) => {
                          event.currentTarget.src = "/s-no-image.png";
                        }}
                      />
                    </div>

                    {/* Name & location */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-1">
                        <h3 className="text-base font-semibold truncate">
                          {supplier.name}
                        </h3>

                        {supplier.verified && (
                          <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        )}
                      </div>

                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <MapPin className="h-3.5 w-3.5" />
                        {supplier.location}
                      </div>
                    </div>
                  </div>

                  <p className="mt-4 min-h-[34px] text-xs leading-[1.45] text-muted-foreground line-clamp-2">
                    {supplier.mainProducts}
                  </p>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-slate-50 px-2.5 py-2 dark:bg-slate-800/70">
                      <span className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                        <Factory className="h-3 w-3" />
                        Catalog
                      </span>
                      <span className="mt-1 block text-xs font-semibold text-foreground">
                        {supplier.products} products
                      </span>
                    </div>
                    <div className="rounded-lg bg-slate-50 px-2.5 py-2 text-right dark:bg-slate-800/70">
                      <span className="block text-[10px] font-medium text-muted-foreground">
                        Established
                      </span>
                      <span className="mt-1 block text-xs font-semibold text-foreground">
                        {supplier.yearEstablished}
                      </span>
                    </div>
                  </div>

                  <div className="mt-4 flex items-center gap-1.5 border-t border-slate-100 pt-3 text-sm font-medium text-primary dark:border-slate-800">
                    <Building2 className="h-3.5 w-3.5" />
                    View supplier profile
                  </div>
                </Link>
              ))
            ) : (
              <SectionEmptyState
                icon={Building2}
                eyebrow="Supplier network"
                title="Be among the first suppliers buyers discover"
                description="Create a supplier account to showcase your catalog and start building visibility with sourcing teams."
                action={{
                  label: "Become a supplier",
                  href: "/auth/register?type=supplier",
                }}
              />
            )}
          </div>
        </div>
      </section>

      {/* TRUST FACTORS */}
      <section className="border-t border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
        <div className="container mx-auto px-4 py-12 sm:px-6 sm:py-14">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0F1B35]">
              Why DoExim
            </p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight text-[#0F1B35] dark:text-white sm:text-3xl">
              Built for clearer sourcing decisions
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Find the information and support you need before you contact a supplier.
            </p>
          </div>

          <div className="mx-auto mt-8 grid max-w-5xl gap-4 md:grid-cols-3">
            {trustFactors.map((factor) => (
              <div
                key={factor.title}
                className="rounded-2xl border border-slate-200 bg-slate-50/70 p-5 dark:border-slate-800 dark:bg-slate-900/60"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#0F1B35] text-white dark:bg-amber-400 dark:text-slate-950">
                  <factor.icon className="h-5 w-5 stroke-[1.8]" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-[#0F1B35] dark:text-white">
                  {factor.title}
                </h3>
                <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                  {factor.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section
        className="
  relative overflow-hidden
  border-y border-yellow-300
  bg-[#f4d03f]
  text-[#0F1B35]
"
      >
        <div className="pointer-events-none absolute -left-16 top-1/2 h-48 w-48 -translate-y-1/2 rounded-full bg-white/45 blur-3xl" />
        <div className="pointer-events-none absolute -right-12 -top-16 h-56 w-56 rounded-full border-[26px] border-[#d7b62a]/35" />
        <div className="container mx-auto px-4 sm:px-6 py-14">
          <div
            className="
      relative
      flex flex-col
      lg:flex-row
      items-start lg:items-center
      justify-between
      gap-6
    "
          >
            {/* Left: Message */}
            <div className="max-w-xl">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0F1B35]">
                DoExim marketplace
              </p>
              <h2 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">
                Ready to grow your business?
              </h2>
              <p className="mt-2 text-sm leading-6 text-[#0F1B35]/80">
                Buy directly from verified suppliers or reach global buyers with
                confidence.
              </p>
            </div>

            {/* Right: Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                asChild
                size="sm"
                className="
            gap-2
            bg-[#0F1B35] text-white
            hover:bg-[#182950]
          "
              >
                <Link href="/welcome/products">
                  <Search className="h-4 w-4" />
                  Start buying
                </Link>
              </Button>

              <Button
                asChild
                size="sm"
                className="
            gap-2
            border border-[#0F1B35]/20 bg-white/80 text-[#0F1B35]
            hover:bg-white
          "
              >
                <Link href="/auth/register?type=supplier">
                  <Store className="h-4 w-4" />
                  Start selling
                </Link>
              </Button>

              <Button
                asChild
                size="sm"
                variant="ghost"
                className="
            gap-1
            text-[#0F1B35]
            hover:bg-[#0F1B35]/10 hover:text-[#0F1B35]
          "
              >
                <Link href="/welcome/faq">
                  Learn more
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
